/**
 * Unsaved Changes Dialog
 * Confirmation dialog shown when switching operators with unsaved changes
 */

import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { Button } from '@/core/components/ui/button';
import { AlertTriangle, Loader2 } from 'lucide-react';

export interface UnsavedChangesDialogProps {
  /** Whether the dialog is open */
  open: boolean;

  /** Name of the operator being switched to */
  targetOperatorName: string;

  /** Handler for saving current changes before switching */
  onSave: () => Promise<void>;

  /** Handler for discarding changes and switching */
  onDiscard: () => void;

  /** Handler for canceling the switch */
  onCancel: () => void;
}

/**
 * Dialog that asks user what to do with unsaved changes when switching operators
 */
export function UnsavedChangesDialog({
  open,
  targetOperatorName,
  onSave,
  onDiscard,
  onCancel,
}: UnsavedChangesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Unsaved Changes
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left space-y-2">
            <p>
              You have unsaved changes to the current operator's configuration.
            </p>
            <p>
              Switching to <span className="font-semibold">{targetOperatorName}</span> will lose these changes unless you save them first.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onDiscard}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            Discard & Switch
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save & Switch'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
